import api from '../../common/api/api';
import graphqlClient from '../../common/api/graphql';
import { UpdateBotSettings } from '../../common/queries/queries';
import { writeData } from './restApi';

// Ola 2: 'true' reads bot settings from main_service REST instead of Hasura.
export const isBotSettingsRest = (): boolean => process.env.NEXT_PUBLIC_REST_BOT_SETTINGS === 'true';

// Own flag for the full bot (GetBotByPk): the most used read, so it rolls back on its own.
export const isBotDetailRest = (): boolean => process.env.NEXT_PUBLIC_REST_BOT_DETAIL === 'true';

type Row = Record<string, any>;

async function getRest<T>(path: string, params?: Record<string, string>): Promise<T> {
  const res = await api.get(path, params);
  if (!res.success || !res.data) throw new Error(`${path} ${res.status ?? ''}`.trim());
  return res.data as T;
}

// The id can come from the URL: encode it so it can't walk to another API path.
const bot = (botId: string, sub = '') => `bots/${encodeURIComponent(botId)}${sub}`;

const getItems = async (path: string, params?: Record<string, string>): Promise<Row[]> => (
  (await getRest<{ items: Row[] }>(path, params)).items
);

// Sales get onboarding/prompt as null; integrations come with their secrets as booleans.
export const fetchBotRest = (botId: string) => getRest<Row>(bot(botId));

// No tokens: null when the bot has no WhatsApp link.
export const fetchBotWhatsappRest = async (botId: string): Promise<Row | null> => {
  const wa = await getRest<Row>(bot(botId, '/whatsapp'));
  return wa.configured ? wa : null;
};

export const fetchChatConfigRest = async (botId: string, configType: string): Promise<Row | null> => (
  (await getItems(bot(botId, '/chat-configs'), { config_type: configType }))[0] ?? null
);

export const fetchBotPromptsRest = (botId: string) => getItems(bot(botId, '/prompts'));

// Ola 3: 'true' saves the bot's settings through REST (staff only).
// The whole object is replaced, as UpdateBotSettings did.
export const isBotSettingsWriteRest = (): boolean => (
  process.env.NEXT_PUBLIC_REST_WRITE_BOT_SETTINGS === 'true'
);

export async function saveBotSettings(botId: string, settings: Row): Promise<Row> {
  if (isBotSettingsWriteRest()) {
    return (await writeData('put', bot(botId, '/settings'), { settings })).settings;
  }
  const { data } = await graphqlClient.mutate({
    mutation: UpdateBotSettings,
    variables: { id: botId, settings },
    fetchPolicy: 'no-cache',
  });
  return data?.update_bots_by_pk?.settings ?? settings;
}
